import type { ISODate, SchedulerZoom } from '@interfaces/IScheduler';
import { parseISO, differenceInCalendarDays, addDays as addDaysFn, format } from 'date-fns';
import {
  localizedDayNumber,
  localizedMonth,
  localizedMonthYear,
  localizedQuarterLabel,
  type CalendarLang,
} from './japaneseCalendar';

export interface ISchedulerViewMode {
  id: SchedulerZoom;
  pxPerDay: number;
  snapDays: number;
  tick: 'day' | 'week' | 'month';
  period: 'month' | 'quarter';
  /** Labels are in the UI language — the header passes `i18n.language`. */
  formatTick: (date: Date, lang: CalendarLang) => string;
  formatPeriod: (date: Date, lang: CalendarLang) => string;
}

export const VIEW_MODES: ISchedulerViewMode[] = [
  {
    id: 'day',
    pxPerDay: 48,
    snapDays: 1,
    tick: 'day',
    period: 'month',
    formatTick: localizedDayNumber,
    formatPeriod: localizedMonthYear,
  },
  {
    id: 'week',
    pxPerDay: 24,
    snapDays: 1,
    tick: 'week',
    period: 'month',
    formatTick: localizedDayNumber,
    formatPeriod: localizedMonthYear,
  },
  {
    // Widened from 6 → 10 px/day so the month view shows ~5 months across the
    // canvas instead of ~8 — bigger columns, more room per month.
    id: 'month',
    pxPerDay: 10,
    snapDays: 7,
    tick: 'month',
    period: 'quarter',
    formatTick: localizedMonth,
    formatPeriod: localizedQuarterLabel,
  },
];

export function viewMode(zoom: SchedulerZoom): ISchedulerViewMode {
  const found = VIEW_MODES.find((viewMode) => viewMode.id === zoom);
  if (found) return found;
  // Defensive: stale persisted state or a widened `SchedulerZoom` enum should
  // not crash the gantt. Fall back to the canonical default.
  const fallback = VIEW_MODES.find((viewMode) => viewMode.id === 'week');
  if (!fallback) throw new Error('VIEW_MODES is missing the "week" entry — refactor regression.');
  return fallback;
}

// Backward-compatible derived exports
export const PX_PER_DAY: Record<SchedulerZoom, number> = Object.fromEntries(
  VIEW_MODES.map((viewMode) => [viewMode.id, viewMode.pxPerDay]),
) as Record<SchedulerZoom, number>;

export function daysBetween(from: ISODate, to: ISODate): number {
  return differenceInCalendarDays(parseISO(to), parseISO(from));
}

export function addDays(date: ISODate, delta: number): ISODate {
  return format(addDaysFn(parseISO(date), delta), 'yyyy-MM-dd');
}

// `dateToX` is the hottest call in the scheduler (every header cell, grid
// tick, weekend column, and bar rect goes through it) and `origin` is
// identical across thousands of consecutive calls within one layout pass — a
// one-slot cache halves the ISO parsing on every hot path. `date` varies per
// call and stays a direct parse.
let cachedOriginIso: ISODate | null = null;
let cachedOriginDate = new Date(0);

// `scale` is the geometric zoom factor (1 = 100%). It multiplies px-per-day so
// the WHOLE grid magnifies uniformly while every position stays in real pixels —
// drag/scroll/virtualization need no compensation, they just see bigger numbers.
export function dateToX(date: ISODate, zoom: SchedulerZoom, origin: ISODate, scale = 1): number {
  if (origin !== cachedOriginIso) {
    cachedOriginIso = origin;
    cachedOriginDate = parseISO(origin);
  }
  return differenceInCalendarDays(parseISO(date), cachedOriginDate) * PX_PER_DAY[zoom] * scale;
}

export function xToDate(x: number, zoom: SchedulerZoom, origin: ISODate, scale = 1): ISODate {
  const days = Math.floor(x / (PX_PER_DAY[zoom] * scale));
  return addDays(origin, days);
}

/**
 * How much a short date range stretches the time axis: 1 normally, and — when
 * a range at the current density would be narrower than the visible canvas —
 * whatever makes it fill the canvas exactly. Only the horizontal axis
 * stretches (px per day); rows, text and the granularity keep the user's zoom.
 * A range wider than the canvas keeps the user's density and scrolls.
 */
export function rangeStretch(
  viewRange: { dateFrom: ISODate; dateTo: ISODate } | null,
  zoom: SchedulerZoom,
  zoomScale: number,
  canvasWidthPx: number,
): number {
  if (!viewRange) return 1;
  const rangePx = (daysBetween(viewRange.dateFrom, viewRange.dateTo) + 1) * PX_PER_DAY[zoom] * zoomScale;
  return Math.max(1, canvasWidthPx / rangePx);
}

/** Geometric zoom bounds (50%–250%) + step for the toolbar stepper / shortcuts. */
export const MIN_ZOOM_SCALE = 0.3;
export const MAX_ZOOM_SCALE = 2.5;
export const ZOOM_STEP = 0.1;

/** Clamp + round (to 2dp) a zoom factor into the supported range. */
export function clampZoomScale(scale: number): number {
  return Math.min(MAX_ZOOM_SCALE, Math.max(MIN_ZOOM_SCALE, Math.round(scale * 100) / 100));
}

/**
 * Returns the pixel height the timeline header occupies for a given zoom.
 *
 * - Month zoom: three rows (quarter / month / week-marker) = 60px.
 * - Day / Week zoom: three rows (period + weekday + day-number) = 60px,
 *   plus an extra 16px for the rokuyō row when the JP calendar is active.
 *
 * `withRokuyo` toggles the rokuyō row — the caller passes `true` only when
 * the user's UI language is Japanese (rokuyō is JP-specific information).
 */
export function headerHeightFor(zoom: SchedulerZoom, withRokuyo: boolean = false): number {
  if (zoom === 'month') return 60;
  return withRokuyo ? 76 : 60;
}
